import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import { cx } from '@/lib/cx';
import { changeAbs } from '@/lib/format';
import { trendOf, type Sentiment, type Trend } from '@/lib/kpi';

const SENTIMENT_CLASS: Record<Sentiment, string> = {
  good: 'bg-up/12 text-up',
  bad: 'bg-down/12 text-down',
  neutral: 'bg-muted text-neutral',
};

const SENTIMENT_WORD: Record<Sentiment, string> = { good: 'good', bad: 'bad', neutral: 'neutral' };

/** Change vs the previous period: the arrow shows direction, the colour shows good/bad. */
export function ChangePill({
  pct,
  trend,
  sentiment,
  comparedTo = 'previous period',
  size = 'md',
}: {
  pct: number | null | undefined;
  trend?: Trend;
  sentiment?: Sentiment;
  comparedTo?: string;
  size?: 'sm' | 'md';
}) {
  const t = trend ?? trendOf(pct);
  const s = sentiment ?? 'neutral';
  if (t === 'none' || pct === null || pct === undefined) {
    return (
      <span className={cx('inline-flex items-center rounded-full bg-muted font-medium text-muted-ink', size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-xs')}>
        <span aria-hidden>—</span>
        <span className="sr-only">No comparison with the {comparedTo}</span>
      </span>
    );
  }
  const Icon = t === 'up' ? ArrowUpRight : t === 'down' ? ArrowDownRight : ArrowRight;
  const word = t === 'up' ? 'Up' : t === 'down' ? 'Down' : 'Flat,';
  return (
    <span
      data-sentiment={s}
      data-trend={t}
      className={cx(
        'tabular inline-flex items-center gap-0.5 rounded-full font-semibold',
        size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-xs',
        SENTIMENT_CLASS[s],
      )}
    >
      <Icon className={size === 'sm' ? 'size-3' : 'size-3.5'} strokeWidth={2.25} aria-hidden />
      <span aria-hidden>{changeAbs(pct)}</span>
      <span className="sr-only">
        {word} {changeAbs(pct)} vs the {comparedTo} ({SENTIMENT_WORD[s]})
      </span>
    </span>
  );
}
